import React from "react";
import Image from "next/image";

/**
 * "Network Traffic Simulation" — the artwork is a circuit-board car whose traces
 * are labelled with OT protocols (OPC, SMB, APC, FTP, HTTP, HTTPS), so the
 * illustration animates by running traffic through those traces: a packet
 * crosses, its protocol name rides along with it, and the line it is crossing
 * lights up cyan for the duration.
 *
 * Cyan is a state here, not a property. The artwork ships with two permanently
 * cyan routes; `scripts/extract-network-traffic-layers.mjs` drops them back to
 * trace grey so every lit line comes from this overlay and no route reads as
 * busy forever.
 *
 * Like the attack-simulation radar, the Figma export is one flattened bitmap
 * (its .svg is only a base64 PNG), so nothing in it can be animated in place.
 * The routes below are the trace centrelines measured off `network-traffic.png`
 * (1600x577): the bitmap was skeletonised, the resulting 1px graph walked into
 * polylines, and every corner snapped back to the artwork's own 0/45/90 grid.
 * They sit on the painted lines to within a pixel, so the packets read as light
 * travelling through the existing circuit rather than as a second drawing on
 * top of it.
 */
type Route = {
  d: string;
  /** Polyline length in artwork units; drives every dash figure below. */
  len: number;
  /**
   * Dash period, `gaps * len`. One packet crosses per `dur / gaps` seconds and
   * the trace is empty for the rest of the cycle. `gaps` is recovered as
   * `period / len` and is always 2, 3 or 4 — one keyframe block each.
   */
  period: number;
  dur: number;
  /** Negative, so traffic is mid-flow on the first frame instead of all launching together. */
  delay: number;
  /** Protocol carried on this trace; only the longer routes get one. */
  label?: string;
  /** Whether the whole line lights up cyan while its packet crosses. */
  lit?: boolean;
  /**
   * Cyan in the source artwork before the extraction script neutralised it.
   * Shown permanently lit when motion is off, so a reader who opts out still
   * gets the illustration with its accent rather than an all-grey version.
   */
  wasCyan?: boolean;
};

const ROUTES: Route[] = [
  { d: "M224.5 231 L230 225.5 L266 189 L563 189 L586 212 L837 212 L897 152 L1056 151.5", len: 883.4, period: 1766.8, dur: 12.80, delay: -1.66, label: "OPC", lit: true, wasCyan: true },
  { d: "M120 471.5 L126.5 478 L148 500 L390 500 L412 522 L603 522", len: 504.1, period: 1008.2, dur: 6.07, delay: -4.49, label: "SMB", lit: true, wasCyan: true },
  { d: "M433 19 L987 19 L1027 59", len: 610.6, period: 1221.2, dur: 7.68, delay: -2.69, label: "OPC", lit: true },
  { d: "M494 83 L1033 83 L1039 89", len: 547.5, period: 1095.0, dur: 7.20, delay: -6.91, label: "SMB" },
  { d: "M690 522 L928 522 L950 544 L1141 544 L1194 491", len: 535.1, period: 1070.2, dur: 7.38, delay: -4.21, label: "HTTPS", lit: true },
  { d: "M285 331 L309 307 L484 307 L540 251 L772 251", len: 520.1, period: 1040.2, dur: 7.54, delay: -1.36, label: "HTTP", lit: true },
  { d: "M151 179 L208 122 L450 122 L490 83", len: 378.5, period: 1135.5, dur: 6.84, delay: -5.40, label: "APC" },
  { d: "M956 251 L689 520", len: 379.0, period: 1137.0, dur: 7.15, delay: -2.86, label: "HTTPS", lit: true },
  { d: "M507 520 L750 275", len: 345.1, period: 1035.3, dur: 6.81, delay: -0.07, label: "FTP" },
  { d: "M958 251 L1194 491", len: 336.6, period: 1009.8, dur: 6.96, delay: -4.32, label: "SMB", lit: true },
  { d: "M309 458 L391 458 L567 283", len: 330.2, period: 990.6, dur: 7.18, delay: -1.65, label: "OPC" },
  { d: "M1183 13 L1325 13 L1371 60 L1450 138", len: 318.8, period: 956.4, dur: 5.76, delay: -4.84, label: "APC" },
  { d: "M492 191 L542 242 L782 242", len: 311.4, period: 934.2, dur: 5.88, delay: -2.65, label: "FTP" },
  { d: "M547 190 L576 219 L804 219", len: 269.0, period: 807.0, dur: 5.31, delay: -0.32, label: "HTTP" },
  { d: "M1038 91 L936 91 L819 208", len: 267.5, period: 802.5, dur: 5.53, delay: -3.71, label: "HTTPS" },
  { d: "M748 61 L1016 61", len: 268.0, period: 804.0, dur: 5.83, delay: -1.63, label: "OPC" },
  { d: "M1332 5 L1391 63 L1479 153 L1479 187 L1463 203", len: 265.2, period: 795.6, dur: 4.79, delay: -4.26 },
  { d: "M1052 304 L1296 304", len: 244.0, period: 976.0, dur: 6.14, delay: -3.07, label: "HTTPS", lit: true },
  { d: "M264 337 L286 359 L488 359", len: 233.1, period: 932.4, dur: 6.13, delay: -0.67 },
  { d: "M1323 261 L1226 261 L1133 166", len: 229.9, period: 919.6, dur: 6.34, delay: -4.56 },
  { d: "M1089 119 L1243 119 L1301 178", len: 236.7, period: 946.8, dur: 6.86, delay: -2.26, label: "HTTP" },
  { d: "M670 359 L806 359 L879 286", len: 239.2, period: 956.8, dur: 5.76, delay: -5.41 },
  { d: "M1308 273 L1219 273 L1116 166", len: 237.5, period: 950.0, dur: 5.97, delay: -3.28 },
  { d: "M1051 306 L1201 460 L1223 460", len: 237.0, period: 948.0, dur: 6.24, delay: -1.00 },
  { d: "M1370 61 L1134 61", len: 236.0, period: 944.0, dur: 6.51, delay: -5.01 },
  { d: "M280 173 L302 152 L452 152 L486 187", len: 229.2, period: 916.8, dur: 6.64, delay: -2.52 },
  { d: "M306 334 L513 334", len: 207.0, period: 828.0, dur: 4.99, delay: -4.94 },
  { d: "M248 394 L453 394", len: 205.0, period: 820.0, dur: 5.16, delay: -3.10 },
  { d: "M829 364 L799 394 L635 394", len: 206.4, period: 825.6, dur: 5.43, delay: -1.14 },
  { d: "M1373 445 L1326 492 L1197 492", len: 195.5, period: 782.0, dur: 5.39, delay: -4.42 },
  { d: "M1121 150 L1235 150 L1267 180 L1300 180", len: 190.9, period: 763.6, dur: 5.53, delay: -2.38 },
  { d: "M493 82 L512 61 L656 61", len: 172.3, period: 689.2, dur: 4.15, delay: -0.17 },
  { d: "M748 275 L590 275", len: 158.0, period: 632.0, dur: 3.97, delay: -2.58 },
  { d: "M1195 491 L1269 418 L1312 418", len: 146.9, period: 587.6, dur: 3.87, delay: -1.01 },
];

/**
 * One packet is a single path, not a stack of fading copies: its dashes thin out
 * towards the back, so falling dash density reads as a fading trail. A dash
 * pattern runs along increasing path distance and the packet travels the same
 * way, so the pattern is written back-to-front — the last dash is the bright
 * head, drawn again on top by a second path.
 *
 * Units are artwork units; the card renders 1600 of them into ~560px, so a
 * 100-unit comet is a ~35px streak and the 4-unit head is a ~1.4px line, about
 * twice the weight of the 2-unit traces it runs on.
 */
const COMET = [2, 20, 4, 16, 6, 12, 8, 8, 10, 5, 22];
const COMET_SPAN = COMET.reduce((a, b) => a + b, 0);
/** Comet length, kept proportional to the route so short traces aren't swamped. */
const cometScale = (len: number) => Math.min(112, Math.max(52, len * 0.34)) / COMET_SPAN;

/** Cool near-white for the packet, over the artwork's own #9B9A9A traces. */
const TRAIL_COLOR = "#E3EDEF";
const HEAD_COLOR = "#FFFFFF";
/** The artwork's own accent, so a lit line looks like one it drew itself. */
const LIT_COLOR = "#00ADBE";

/** The wheels bracket the network; each sends a slow radar ping outwards. */
const WHEELS = [
  { cx: 120, cy: 335, delay: "-1.6s" },
  { cx: 1450, cy: 335, delay: "-4.8s" },
];

/**
 * The HUD rings inside each wheel, lifted off the bitmap by
 * `scripts/extract-network-traffic-layers.mjs` so they can turn. Each file holds
 * both wheels and is drawn twice under a clip that keeps one of them, so a ring
 * costs one request instead of two.
 *
 * `turn` is half the sweep and its sign is the direction, so neighbouring rings
 * counter-rotate: the ring rocks from -turn to +turn and back, pausing at each
 * end. Durations are mutually prime-ish, which keeps the three from lining up.
 */
const WHEEL_RINGS = [
  { name: "inner", turn: "16deg", dur: "9s" },
  { name: "mid", turn: "-24deg", dur: "7.5s" },
  { name: "outer", turn: "34deg", dur: "11s" },
] as const;

/**
 * Pivot per wheel as a share of the 1600x577 artwork box (120/1600, 1450/1600,
 * 335/577), so it holds at whatever size the card renders. The clip keeps the
 * half of the file that holds that wheel — both sit far from the 50% seam, and
 * the seam is 680 units from either pivot, so it never swings into view.
 */
const WHEEL_SIDES = [
  { side: "left", origin: "7.5% 58.06%", clip: "inset(0 50% 0 0)", delay: "0s" },
  { side: "right", origin: "90.625% 58.06%", clip: "inset(0 0 0 50%)", delay: "-4.3s" },
] as const;

type Vars = React.CSSProperties & Record<string, string | number>;

const coords = (d: string) => d.match(/-?\d+(?:\.\d+)?/g)!.map(Number);

/**
 * A route drawn right-to-left needs its label counter-rotated, otherwise
 * `offset-rotate: auto` hands it a 180deg heading and the text runs upside down.
 * The extra half turn also flips the label's local -Y back to screen-up, so the
 * same `translate` keeps it on the upper side of the trace either way.
 */
const runsLeftward = (d: string) => {
  const n = coords(d);
  return n[n.length - 2] < n[0];
};

/**
 * Delay that lands the terminator flash on the frame the packet arrives. The
 * flash peaks at 6% of the cycle and the packet lands at `dur / gaps`, so the
 * route's own delay is shifted by the difference and wrapped to stay negative.
 */
const arrivalDelay = (route: Route, gaps: number) => {
  const d = route.delay + route.dur / gaps - 0.06 * route.dur;
  return +(d > 0 ? d - route.dur : d).toFixed(2);
};

const NetworkTrafficFlow = () => (
  <>
    {WHEEL_RINGS.map((ring) =>
      WHEEL_SIDES.map((wheel) => (
        <Image
          key={`${ring.name}-${wheel.side}`}
          src={`/images/OTSecurity/cyber-range/network-traffic-wheel-${ring.name}.png`}
          alt=""
          aria-hidden="true"
          width={1600}
          height={577}
          sizes="(max-width: 1024px) 90vw, 560px"
          className="nt-wheel pointer-events-none absolute inset-0 h-full w-full"
          style={
            {
              "--nt-turn": ring.turn,
              transformOrigin: wheel.origin,
              clipPath: wheel.clip,
              animationDuration: ring.dur,
              animationDelay: wheel.delay,
            } as Vars
          }
        />
      ))
    )}

    <svg
      viewBox="0 0 1600 577"
      fill="none"
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 h-full w-full"
    >
      {WHEELS.map((wheel) => (
        <circle
          key={wheel.cx}
          className="nt-ping"
          cx={wheel.cx}
          cy={wheel.cy}
          r={70}
          stroke={LIT_COLOR}
          strokeWidth={2.5}
          style={{ animationDelay: wheel.delay }}
        />
      ))}

      {ROUTES.map((route, i) => {
        const gaps = Math.round(route.period / route.len);
        const scale = cometScale(route.len);
        const dashes = COMET.map((v) => +(v * scale).toFixed(2));
        const head = dashes[dashes.length - 1];
        const span = +(COMET_SPAN * scale).toFixed(2);
        const headStyle: Vars = {
          "--nt-o0": route.period,
          "--nt-o1": 0,
          strokeDasharray: `${head} ${+(route.period - head).toFixed(2)}`,
        };
        // Shifting the trail back by (span - head) lines its front dash up with
        // the head instead of letting the longer pattern run ahead of it.
        const trailStyle: Vars = {
          "--nt-o0": +(route.period + span - head).toFixed(2),
          "--nt-o1": +(span - head).toFixed(2),
          strokeDasharray: `${dashes.join(" ")} ${+(route.period - span).toFixed(2)}`,
        };
        const end = coords(route.d).slice(-2);
        return (
          <g
            key={i}
            style={{
              animationDuration: `${route.dur}s`,
              animationDelay: `${route.delay}s`,
            }}
          >
            {/* Drawn first so the packet rides on top of its own lit line. One
                dash as long as the route, uncovered as the packet advances. */}
            {route.lit && (
              <path
                className={`nt-lit nt-lit-g${gaps}${route.wasCyan ? " nt-lit-rest" : ""}`}
                d={route.d}
                style={{ "--nt-len": route.len, strokeDasharray: `${route.len} ${route.len}` } as Vars}
              />
            )}
            <path className="nt-packet nt-glow" d={route.d} style={headStyle} />
            <path className="nt-packet nt-trail" d={route.d} style={trailStyle} />
            <path
              className="nt-packet nt-head"
              d={route.d}
              style={{ ...headStyle, stroke: HEAD_COLOR }}
            />
            {route.lit && (
              <circle
                className="nt-node"
                cx={end[0]}
                cy={end[1]}
                r={7}
                stroke={LIT_COLOR}
                strokeWidth={3}
                style={{
                  animationDuration: `${route.dur}s`,
                  animationDelay: `${arrivalDelay(route, gaps)}s`,
                }}
              />
            )}
            {route.label && (
              <text
                className={`nt-label nt-label-g${gaps}`}
                x={0}
                y={0}
                textAnchor="middle"
                dominantBaseline="middle"
                fill={TRAIL_COLOR}
                style={{
                  offsetPath: `path("${route.d}")`,
                  offsetRotate: runsLeftward(route.d) ? "auto 180deg" : "auto",
                }}
              >
                {route.label}
              </text>
            )}
          </g>
        );
      })}
    </svg>

    <style jsx>{`
      /* The <g> declares duration and delay but runs no animation itself; every
         part of a packet inherits those values, so the lit line, the comet, the
         label and the terminator flash can never drift apart. */
      .nt-packet {
        stroke: #E3EDEF;
        stroke-linecap: round;
        animation-name: ntFlow;
        animation-duration: inherit;
        animation-delay: inherit;
        animation-timing-function: linear;
        animation-iteration-count: infinite;
      }
      @keyframes ntFlow {
        from {
          stroke-dashoffset: var(--nt-o0);
        }
        to {
          stroke-dashoffset: var(--nt-o1);
        }
      }
      /* Widening the same dash is cheaper than a blur filter and holds up when
         the card is scaled down on mobile. */
      .nt-glow {
        stroke-width: 11;
        opacity: 0.18;
      }
      .nt-trail {
        stroke-width: 2.6;
        opacity: 0.6;
      }
      .nt-head {
        stroke-width: 4;
      }

      /* A lit line is one dash as long as the route: its offset walks from the
         full length to zero, so cyan follows the packet head down the trace,
         holds once the packet is through, then fades back to grey. 2.6 units
         just covers the 2-unit trace underneath, rim included. */
      .nt-lit {
        stroke: #00ADBE;
        stroke-width: 2.6;
        stroke-linecap: round;
        opacity: 0;
        animation-duration: inherit;
        animation-delay: inherit;
        animation-timing-function: linear;
        animation-iteration-count: infinite;
      }
      .nt-lit-g2 {
        animation-name: ntLit2;
      }
      .nt-lit-g3 {
        animation-name: ntLit3;
      }
      .nt-lit-g4 {
        animation-name: ntLit4;
      }
      /* Crossing takes 1/gaps of the cycle: 50%, 33.33% or 25%. */
      @keyframes ntLit2 {
        0% {
          stroke-dashoffset: var(--nt-len);
          opacity: 0;
        }
        3% {
          opacity: 1;
        }
        50%,
        65% {
          stroke-dashoffset: 0;
          opacity: 1;
        }
        82%,
        100% {
          stroke-dashoffset: 0;
          opacity: 0;
        }
      }
      @keyframes ntLit3 {
        0% {
          stroke-dashoffset: var(--nt-len);
          opacity: 0;
        }
        3% {
          opacity: 1;
        }
        33.33%,
        53% {
          stroke-dashoffset: 0;
          opacity: 1;
        }
        76%,
        100% {
          stroke-dashoffset: 0;
          opacity: 0;
        }
      }
      @keyframes ntLit4 {
        0% {
          stroke-dashoffset: var(--nt-len);
          opacity: 0;
        }
        2% {
          opacity: 1;
        }
        25%,
        47% {
          stroke-dashoffset: 0;
          opacity: 1;
        }
        73%,
        100% {
          stroke-dashoffset: 0;
          opacity: 0;
        }
      }

      /* The protocol name rides its own packet: offset-path walks the same
         centreline, offset-rotate auto lays the text along the trace the way the
         artwork's baked-in labels are, and the translate lifts it clear of the
         line and drops it just behind the head. transform-box fill-box is what
         makes offset-anchor auto resolve to the text's own centre — without it
         an SVG element anchors on the whole view box. */
      .nt-label {
        font-family: var(--font-avenir), sans-serif;
        font-size: 13.5px;
        font-weight: 300;
        letter-spacing: 0.3px;
        opacity: 0;
        transform-box: fill-box;
        transform-origin: center;
        transform: translate(-20px, -10px);
        animation-duration: inherit;
        animation-delay: inherit;
        animation-timing-function: linear;
        animation-iteration-count: infinite;
      }
      .nt-label-g2 {
        animation-name: ntLabel2;
      }
      .nt-label-g3 {
        animation-name: ntLabel3;
      }
      .nt-label-g4 {
        animation-name: ntLabel4;
      }
      @keyframes ntLabel2 {
        0% {
          offset-distance: 0%;
          opacity: 0;
        }
        4%,
        44% {
          opacity: 0.9;
        }
        50%,
        100% {
          offset-distance: 100%;
          opacity: 0;
        }
      }
      @keyframes ntLabel3 {
        0% {
          offset-distance: 0%;
          opacity: 0;
        }
        2.7%,
        29.3% {
          opacity: 0.9;
        }
        33.33%,
        100% {
          offset-distance: 100%;
          opacity: 0;
        }
      }
      @keyframes ntLabel4 {
        0% {
          offset-distance: 0%;
          opacity: 0;
        }
        2%,
        22% {
          opacity: 0.9;
        }
        25%,
        100% {
          offset-distance: 100%;
          opacity: 0;
        }
      }

      /* Each ring rocks one way then the other, holding briefly at both ends so
         the wheels read as instruments being adjusted, not as free-spinning.
         :global is required — the rings are <Image>, and styled-jsx only scopes
         DOM elements, so a plain .nt-wheel rule would never match the <img>. */
      :global(.nt-wheel) {
        animation-name: ntWheelTurn;
        animation-timing-function: ease-in-out;
        animation-iteration-count: infinite;
      }
      @keyframes ntWheelTurn {
        0%,
        5% {
          transform: rotate(calc(var(--nt-turn) * -1));
        }
        45%,
        55% {
          transform: rotate(var(--nt-turn));
        }
        95%,
        100% {
          transform: rotate(calc(var(--nt-turn) * -1));
        }
      }

      .nt-ping {
        fill: none;
        transform-box: fill-box;
        transform-origin: center;
        animation: ntPing 6.4s ease-out infinite;
      }
      @keyframes ntPing {
        0% {
          opacity: 0;
          transform: scale(0.35);
        }
        14% {
          opacity: 0.36;
        }
        100% {
          opacity: 0;
          transform: scale(1.9);
        }
      }

      .nt-node {
        fill: none;
        opacity: 0;
        transform-box: fill-box;
        transform-origin: center;
        animation-name: ntNode;
        animation-timing-function: linear;
        animation-iteration-count: infinite;
      }
      @keyframes ntNode {
        0% {
          opacity: 0;
          transform: scale(0.6);
        }
        6% {
          opacity: 0.9;
          transform: scale(1.35);
        }
        26%,
        100% {
          opacity: 0;
          transform: scale(0.6);
        }
      }

      @media (prefers-reduced-motion: reduce) {
        /* The rings were lifted out in place, so parking them at rotate(0)
           puts the wheels back exactly as the artwork drew them. */
        :global(.nt-wheel) {
          animation: none;
        }
        /* No motion means no traffic — leave the artwork as drawn. */
        .nt-packet,
        .nt-lit,
        .nt-label,
        .nt-ping,
        .nt-node {
          animation: none;
          display: none;
        }
        /* ...except the two routes the artwork itself drew cyan. With the
           animation off, dashoffset rests at 0, so the dash covers the whole
           route and the illustration matches the original export. */
        .nt-lit-rest {
          display: inline;
          opacity: 1;
        }
      }
    `}</style>
  </>
);

export default NetworkTrafficFlow;
